import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import type { EvidenceRef } from '../../../Generated/EvidenceRef.ts'
import { buildDossierUrl, getEvidenceDetail } from '../../../Repository/Chat/ChatRepository.js'

/** Icon + human label per evidence kind. Shared by the "Evidence used" panel and the inline `[eN]` chips. */
export const EVIDENCE_KIND_META: Record<string, { icon: string; label: string }> = {
  report: { icon: '📄', label: 'Report' },
  otu: { icon: '🧬', label: 'OTU' },
  sample: { icon: '🧪', label: 'Sample' },
  citation: { icon: '📚', label: 'Literature' }
}

/** Fallback for an unexpected kind — keep the raw kind string visible rather than guessing. */
export function evidenceKindMeta(kind: string): { icon: string; label: string } {
  return EVIDENCE_KIND_META[kind] ?? { icon: '🔎', label: kind }
}

/**
 * Human labels for known evidence/detail field keys. Keeps the card customer-facing: no camelCase, no internal
 * jargon. Unknown keys fall through to {@link prettifyKey} (camelCase/snake_case → "Spaced Title") so the card
 * stays data-driven — a new backend field still renders, just tidied — rather than silently dropping it.
 */
const FIELD_LABELS: Record<string, string> = {
  reads: 'Reads',
  rawReads: 'Reads',
  readsTotal: 'Reads',
  totalReads: 'Reads',
  readCount: 'Reads',
  grade: 'Grade',
  gradePercentage: 'Grade',
  gradeBucket: 'Grade',
  source: 'Source',
  blastSource: 'Source',
  taxonomySource: 'Source',
  primer: 'Primer',
  primerName: 'Primer',
  clusterSize: 'Cluster size',
  representation: 'Representation',
  representationPct: 'Representation',
  targetTaxonomy: 'Target taxonomy',
  targetRegion: 'Target region',
  species: 'Species',
  taxon: 'Taxon',
  otuId: 'OTU',
  sampleLabel: 'Sample',
  hitPercentage: 'Hit %',
  queryCoverage: 'Query coverage',
  evaluatedAt: 'Evaluated at',
  dbVersionSet: 'Reference DB versions',
  expectationValue: 'E-value'
}

/**
 * Purely-internal machinery keys — UUIDs, provenance commit ids, and the SPIN control hints that thread the
 * deep-view fetch — that carry no meaning for a customer. Hidden from the card; still present in the ref so
 * {@link DeepView} can read `spinAlgorithm`/`spinAtTime` for the access-gated fetch.
 */
const HIDDEN_FIELD_KEYS = new Set([
  'otuUuid',
  'spinAlgorithm',
  'spinAtTime',
  'pipelineCommit',
  'gitCommitId',
  'reportId',
  'scopeId',
  'anchor',
  // citation: the paper link is surfaced as the "Open paper →" action in DeepView, not as a raw text row.
  'link'
])

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** camelCase / snake_case key → "Spaced Title" fallback for keys without an explicit label. */
function prettifyKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Human label for a field key: known mapping first, then a prettified fallback. */
export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? prettifyKey(key)
}

/** True when a field should be hidden from the customer-facing card: an internal key, or a raw-UUID value. */
export function isHiddenField(key: string, value: unknown): boolean {
  if (HIDDEN_FIELD_KEYS.has(key)) return true
  if (typeof value === 'string' && UUID_RE.test(value)) return true
  return false
}

type EvidenceViewProps = {
  evidence: EvidenceRef
  /** Conversation scope, threaded from ChatPage — needed to fetch + access-gate the deep view. */
  scopeType?: 'order' | 'project'
  scopeId?: string
}

/** True for a plain object value (not null, not an array) — used to render nested detail sections. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** One row of the deep-detail body: a nested object, an array of sub-objects, or a scalar key→value line. */
function DetailRow({ label, value }: { label: string; value: unknown }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    return (
      <span className="mt-1 block">
        <span className="block font-semibold opacity-70">{label}</span>
        {value.map((item, i) => (
          <span key={i} className="mt-0.5 block rounded border border-gray-100 p-1">
            {isRecord(item) ? <DetailBody detail={item} /> : <span className="break-all">{String(item)}</span>}
          </span>
        ))}
      </span>
    )
  }
  if (isRecord(value)) {
    return (
      <span className="mt-1 block">
        <span className="block font-semibold opacity-70">{label}</span>
        <span className="block pl-2">
          <DetailBody detail={value} />
        </span>
      </span>
    )
  }
  return (
    <span className="flex justify-between gap-2 border-t border-gray-100 py-0.5">
      <span className="opacity-60">{label}</span>
      <span className="break-all text-right">{String(value)}</span>
    </span>
  )
}

/**
 * Generic key→value / section render of a fetched detail object. Skips the internal `kind`, empty values, and
 * internal-only keys/raw-UUID values; relabels the rest to human labels via {@link fieldLabel}.
 */
function DetailBody({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail).filter(
    ([k, v]) => k !== 'kind' && v !== null && v !== undefined && v !== '' && !isHiddenField(k, v)
  )
  if (entries.length === 0) return <span className="block opacity-60">No further detail available.</span>
  return (
    <span className="block">
      {entries.map(([k, v]) => (
        <DetailRow key={k} label={fieldLabel(k)} value={v} />
      ))}
    </span>
  )
}

/**
 * The "view the actual entity" deep-view action attached under the free-tier card (Epic A #7). Kind-aware:
 * - `otu` / `sample` → a "Show full detail" button that lazy-fetches the access-gated detail endpoint for
 *   `(kind, anchor, scope)` on click (loading + error states), then renders it inline; never refetches once
 *   loaded. Scrolls inside the card if long — no page/horizontal scroll.
 * - `report` → links to the existing order page for the conversation's order (only resolvable in ORDER scope;
 *   a report→order mapping is NOT carried on the ref, so in PROJECT scope this stays a best-effort note rather
 *   than inventing one).
 */
function DeepView({ evidence, scopeType, scopeId }: EvidenceViewProps) {
  const [detail, setDetail] = useState<Record<string, unknown> | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  if (evidence.kind === 'report') {
    // A report maps to an order; the ref carries only reportId, so only the ORDER-scoped case is resolvable
    // (the order the chat is scoped to). Don't invent a report→order mapping in PROJECT scope.
    if (scopeType === 'order' && scopeId) {
      return (
        <span className="mt-2 block border-t border-gray-100 pt-2">
          <Link to={`/order/${scopeId}`} className="link link-primary">
            Open order page →
          </Link>
        </span>
      )
    }
    return (
      <span className="mt-2 block border-t border-gray-100 pt-2 opacity-60">
        Open the source order from its order page for the full report.
      </span>
    )
  }

  if (evidence.kind === 'citation') {
    // Literature is a GLOBAL corpus (no scope-gated fetch); the deep-view action is the CC-BY attribution link
    // out to the paper (DOI, falling back to the PMC page). The link lives on the ref's `link` field.
    const link = typeof evidence.fields?.link === 'string' ? evidence.fields.link : undefined
    if (!link) return null
    return (
      <span className="mt-2 block border-t border-gray-100 pt-2">
        <a href={link} target="_blank" rel="noopener noreferrer" className="link link-primary">
          Open paper →
        </a>
      </span>
    )
  }

  const canFetch = (evidence.kind === 'otu' || evidence.kind === 'sample') && !!scopeType && !!scopeId
  if (!canFetch) return null

  // SPIN threading (Epic A #7): a SPIN-mode OTU ref anchors on a spin id and carries the algorithm/atTime it was
  // captured with; forward them so the deep-view resolves via the SPIN path (classic OTUs have neither field).
  const spinAlgorithm = typeof evidence.fields?.spinAlgorithm === 'string' ? evidence.fields.spinAlgorithm : undefined
  const spinAtTime = typeof evidence.fields?.spinAtTime === 'string' ? evidence.fields.spinAtTime : undefined

  const loadDetail = () => {
    if (loading || detail) return
    setLoading(true)
    setError(undefined)
    getEvidenceDetail(evidence.kind, scopeType as string, scopeId as string, evidence.anchor, spinAlgorithm, spinAtTime)
      .then((d) => setDetail(d))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load detail'))
      .finally(() => setLoading(false))
  }

  return (
    <span className="mt-2 block border-t border-gray-100 pt-2">
      {!detail && (
        <button type="button" className="btn btn-xs btn-outline" onClick={loadDetail} disabled={loading}>
          {loading ? 'Loading…' : 'Show full detail'}
        </button>
      )}
      {/* Export dossier (Epic A #12): opens the server-rendered, self-contained HTML evidence document in a new
          tab for browser print-to-PDF. OTU only (the backend serves kind=otu); scope + SPIN threading reuse the
          same fields the deep-view fetch does. A disabled/absent dossier config just 404s the opened tab. */}
      {evidence.kind === 'otu' && (
        <button
          type="button"
          className="btn btn-xs btn-outline ml-1"
          onClick={() =>
            window.open(
              buildDossierUrl(scopeType as string, scopeId as string, evidence.anchor, spinAlgorithm, spinAtTime),
              '_blank',
              'noopener'
            )
          }
        >
          Export dossier
        </button>
      )}
      {error && <span className="mt-1 block text-error">{error}</span>}
      {detail && (
        <span className="mt-1 block max-h-64 overflow-y-auto">
          <DetailBody detail={detail} />
        </span>
      )}
    </span>
  )
}

/**
 * Free-tier evidence card: renders ONE {@link EvidenceRef} as a compact, self-contained card — a kind
 * icon/label, the ref's `label`, and its `fields` as a small key→value list. The card itself renders ONLY what
 * is already in the ref (no network fetch); a kind-aware {@link DeepView} action below it optionally loads the
 * real entity detail on demand. Shared by two callers: the Tier-1 "Evidence used" panel (expanded inline) and
 * the Tier-2 inline `[eN]` chips (shown in a popover anchored to the chip).
 *
 * Built entirely from `<span>` elements (styled as blocks), plus a `<button>`/`<a>` for the deep-view action
 * (both phrasing content), so the card is valid inside BOTH a `<p>` — the chips live inside react-markdown
 * answer paragraphs — and the panel's block layout, without invalid div-in-paragraph nesting.
 */
export function EvidenceView({ evidence, scopeType, scopeId }: EvidenceViewProps) {
  const meta = evidenceKindMeta(evidence.kind)
  const fields = Object.entries(evidence.fields ?? {}).filter(
    ([k, v]) =>
      v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0) && !isHiddenField(k, v)
  )

  return (
    <span className="block w-64 max-w-[80vw] rounded-lg border border-gray-200 bg-base-100 p-3 text-left text-xs shadow-lg">
      <span className="flex items-center gap-1 font-semibold">
        <span aria-hidden="true">{meta.icon}</span>
        <span className="badge badge-ghost badge-sm">{meta.label}</span>
      </span>
      <span className="mt-1 block break-words font-medium">{evidence.label}</span>
      {fields.length > 0 && (
        <span className="mt-1 block">
          {fields.map(([k, v]) => (
            <span key={k} className="flex justify-between gap-2 border-t border-gray-100 py-0.5">
              <span className="opacity-60">{fieldLabel(k)}</span>
              <span className="break-all text-right">{Array.isArray(v) ? v.join('; ') : String(v)}</span>
            </span>
          ))}
        </span>
      )}
      {/* Deep view — "open the full entity". OTU/sample lazy-fetch the access-gated
          /api/chat/evidence/:kind endpoint (delegates to existing chat services — no new SQL); report
          links to the order page. */}
      <DeepView evidence={evidence} scopeType={scopeType} scopeId={scopeId} />
    </span>
  )
}
